import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import "./DeviceDetail.css";
import "./DeviceList.css";
import { API_BASE, API_BASE_IOTHUB, authHeaders } from '../constants/api';
import { fetchListing } from '../utils/thingsboardEdition';
import { severityLabels } from '../constants/alarmConstants';
import { AlarmStatusDropdown, SearchDropdown } from '../UI-Elements';
import { useDropdownClose } from '../hooks/useDropdownClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useTbLiveData } from '../hooks/useTbLiveData';
import { updateAlarmStatusAPI } from '../utils/alarmActions';
import { formatTelemetryTimestamp, formatDateDE } from '../utils/formatDate';
import { useAuth } from '../context/AuthContext';
import Pagination from './Pagination';
import { Zurück, Export, Import, Downlink, Löschen, Bearbeiten, Öffnen, AiAnalyse2, Abbrechen, Speichern } from '../icons';

const ALARM_PAGE_SIZE = 5;

function DeviceDetail({ deviceId: propDeviceId }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const deviceId = propDeviceId || params.deviceId;
  const { authToken: token, iotHubToken } = useAuth();

  const [device, setDevice] = useState(() => {
    const nameFromState = location && location.state && location.state.name;
    return nameFromState ? { name: nameFromState } : null;
  });
  const titleRef = useRef(null);
  const [telemetry, setTelemetry] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [openAlarmStatusMenu, setOpenAlarmStatusMenu] = useState(null);

  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState('success');

  const [loading, setLoading] = useState(true);
  const [liveEnabled, setLiveEnabled] = useState(false);
  const [error, setError] = useState("");

  const [alarmTab, setAlarmTab] = useState('triggered');
  const [alarmRules, setAlarmRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState("");
  const rulesFetchedRef = useRef(false);
  const [alarmsPage, setAlarmsPage] = useState(0);
  const [rulesPage, setRulesPage] = useState(0);

  const [deviceImageUrl, setDeviceImageUrl] = useState(null);
  const [deviceImageLoading, setDeviceImageLoading] = useState(true);

  // Edit device state
  const [allProfiles, setAllProfiles] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editVendorText, setEditVendorText] = useState('');
  const [editSelectedVendor, setEditSelectedVendor] = useState('');
  const [editModelText, setEditModelText] = useState('');
  const [editSelectedModel, setEditSelectedModel] = useState('');
  const [saving, setSaving] = useState(false);
  const editOriginalRef = useRef(null);

  // Delete alarm state
  const [alarmToDelete, setAlarmToDelete] = useState(null);
  const [showAlarmDeleteConfirm, setShowAlarmDeleteConfirm] = useState(false);
  const [deletingAlarm, setDeletingAlarm] = useState(false);
  const alarmDeleteRef = useRef(null);
  useFocusTrap(showAlarmDeleteConfirm, alarmDeleteRef);

  // Delete alarm rule state
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showRuleDeleteConfirm, setShowRuleDeleteConfirm] = useState(false);
  const [deletingRule, setDeletingRule] = useState(false);
  const ruleDeleteRef = useRef(null);
  useFocusTrap(showRuleDeleteConfirm, ruleDeleteRef);

  // Delete device state
  const [showDeviceDeleteConfirm, setShowDeviceDeleteConfirm] = useState(false);
  const [deletingDevice, setDeletingDevice] = useState(false);
  const deviceDeleteRef = useRef(null);
  useFocusTrap(showDeviceDeleteConfirm, deviceDeleteRef);

  const showStatus = (msg, type = 'success') => {
    setStatusType(type);
    setStatusMessage(msg);
    setTimeout(() => setStatusMessage(''), 4000);
  };

  const closeAlarmStatusMenu = useCallback(() => setOpenAlarmStatusMenu(null), []);
  useDropdownClose(openAlarmStatusMenu, closeAlarmStatusMenu, '.alarm-status-wrapper');

  useEffect(() => {
    async function loadAll() {
      try {
        setLoading(true);
        setError("");
        setLiveEnabled(false);

        const headers = authHeaders(token);

        const base = API_BASE;

        // Gerätedetails + TB-Daten parallel laden. Die Kennung des IoT Hub
        // *ist* die ThingsBoard-Geräte-ID — der frühere Auflösungsschritt über
        // /devices/by-tb-id war seit der Umstellung auf ChirpStack als Quelle
        // nur noch ein Umweg über denselben Wert.
        const [
          deviceRes,
          telemetryRes,
          alarmsRes
        ] = await Promise.all([
          fetch(`${API_BASE_IOTHUB}/api/v1/devices/${deviceId}`, { headers: authHeaders(iotHubToken) }),
          fetch(`${base}/plugins/telemetry/DEVICE/${deviceId}/values/timeseries`, { headers }),
          fetch(`${base}/alarms?entityType=DEVICE&entityId=${deviceId}&pageSize=10&page=0`, { headers })
        ]);

        if (!deviceRes.ok) throw new Error(`Laden derGerätedetails fehlgeschlagen (HTTP ${deviceRes.status})`);

        const deviceData = await deviceRes.json();
        const telemetryData = await telemetryRes.json().catch(() => ({}));
        const alarmsData = await alarmsRes.json().catch(() => ({ data: [] }));

        // active + Gruppen über ThingsBoard deviceInfos abfragen. Gruppen gibt
        // es nur in der Professional Edition; in CE bleibt die Liste leer und
        // "active" kommt aus dem entsprechenden Tenant-Endpunkt.
        let active = false;
        let groups = [];
        try {
          const infoRes = await fetchListing('deviceInfos/all', {
            pageSize: 50, page: 0,
            textSearch: deviceData.name || '',
            includeCustomers: true,
          }, token);
          if (infoRes.ok) {
            const infoData = await infoRes.json().catch(() => ({}));
            const match = (infoData.data || []).find(i => i.id?.id === deviceId);
            if (match) {
              active = match.active ?? false;
              groups = (match.groups || [])
                .filter(g => g.name !== 'All')
                .map(g => ({ id: g.id?.id ?? g.id, name: g.name }));
            }
          }
        } catch (e) {
          console.error('deviceInfos fallback failed', e);
        }

        setDevice({
          name: deviceData.name,
          active,
          groups,
          vendor: deviceData.details?.vendor,
          model: deviceData.details?.model,
          firmware: deviceData.details?.firmware,
          region: deviceData.details?.region,
          cs_dev_eui: deviceData.cs_dev_eui,
          //app_key: deviceData.app_key,
          createdTime: deviceData.created_at ? new Date(deviceData.created_at).getTime() : null,
        });

        setTelemetry(telemetryData || []);
        setDashboards([]);

        // Alle Alarme dieses Geräts nach Schweregrad sortieren
        const filtered = (alarmsData.data || []).filter(
          a => a.originator.id === deviceId
        );

        const severityOrd = { CRITICAL: 1, MAJOR: 2, MINOR: 3, WARNING: 4, INDETERMINATE: 5 };
        const statusOrd = { ACTIVE_UNACK: 1, ACTIVE_ACK: 2, CLEARED_ACK: 3 };
        filtered.sort((a, b) => {
          const statusDiff = (statusOrd[a.status] ?? 9) - (statusOrd[b.status] ?? 9);
          if (statusDiff !== 0) return statusDiff;
          return (severityOrd[a.severity] ?? 9) - (severityOrd[b.severity] ?? 9);
        });

        setAlarms(filtered);
        setLiveEnabled(true);


      } catch (err) {
        console.error('Error loading device details:', err);
        setError("Gerätedaten konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    }

    if (deviceId && token) loadAll();
  }, [deviceId, token, iotHubToken]);

  const { liveTelemetry, liveActive } = useTbLiveData({ deviceId, token, enabled: liveEnabled });

  // Live-Telemetrie in den bestehenden telemetry-State mergen (letzter Wert je Schlüssel)
  useEffect(() => {
    if (!liveTelemetry || Object.keys(liveTelemetry).length === 0) return;
    setTelemetry(prev => {
      const next = { ...prev };
      for (const [key, { ts, value }] of Object.entries(liveTelemetry)) {
        next[key] = [{ ts, value }];
      }
      return next;
    });
  }, [liveTelemetry]);

  // Live-Aktiv-Status in den device-State mergen
  useEffect(() => {
    if (liveActive === null) return;
    setDevice(prev => (prev ? { ...prev, active: liveActive } : prev));
  }, [liveActive]);

  // Fetch device image based on device type
  useEffect(() => {
    if (!device?.model || !token) return;
    let cancelled = false;
    async function loadImage() {
      try {
        setDeviceImageLoading(true);
        const searchParams = new URLSearchParams({
          textSearch: device.model,
          pageSize: '50',
          page: '0',
          includeSystemImages: 'true'
        });
        const res = await fetch(
          `${API_BASE}/images?${searchParams.toString()}`,
          { headers: authHeaders(token) }
        );
        if (!res.ok) throw new Error(`images search failed: ${res.status}`);
        const data = await res.json();
        const images = data.data || [];
        if (cancelled) return;
        if (images.length > 0) {
          const img = images[0];
          const imageUrl = img.link
            ? `${API_BASE.replace('/api', '')}${img.link}`
            : img.id?.id
              ? `${API_BASE}/image/${img.id.id}`
              : null;
          if (imageUrl) {
            const imgRes = await fetch(imageUrl, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!imgRes.ok) throw new Error(`image download failed: ${imgRes.status}`);
            if (cancelled) return;
            const blob = await imgRes.blob();
            setDeviceImageUrl(URL.createObjectURL(blob));
          }
        }
      } catch (err) {
        console.error('[DeviceImage] error:', err);
      } finally {
        if (!cancelled) setDeviceImageLoading(false);
      }
    }
    loadImage();
    return () => {
      cancelled = true;
      setDeviceImageUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [device?.type, device?.model, token]);

  // Fetch vendor/model profiles for edit form
  useEffect(() => {
    if (!iotHubToken) return;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API_BASE_IOTHUB}/api/v1/vendors/device-profiles`, {
          headers: authHeaders(iotHubToken),
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        setAllProfiles(Array.isArray(data) ? data : []);
      } catch { /* ignore abort */ }
    })();
    return () => controller.abort();
  }, [iotHubToken]);

  const vendorSuggestions = useMemo(() => {
    const vendors = [...new Set(allProfiles.map(p => p.vendor).filter(Boolean))];
    return vendors.map(v => ({ value: v, label: v }));
  }, [allProfiles]);

  const modelSuggestions = useMemo(() => {
    if (!editSelectedVendor) return [];
    const normalizedVendor = editSelectedVendor.trim().toLowerCase();
    const devices = [...new Set(
      allProfiles
        .filter(p => (p.vendor || '').trim().toLowerCase() === normalizedVendor)
        .map(p => p.device)
        .filter(Boolean)
    )];
    return devices.map(d => ({ value: d, label: d }));
  }, [allProfiles, editSelectedVendor]);

  // Fetch alarm rules lazily when the rules tab is first activated
  useEffect(() => {
    if (alarmTab !== 'rules' || rulesFetchedRef.current || !deviceId || !token) return;
    async function loadRules() {
      try {
        setRulesLoading(true);
        setRulesError("");
        const params = new URLSearchParams({
          pageSize: '50',
          page: '0',
          types: 'ALARM',
          sortProperty: 'createdTime',
          sortOrder: 'DESC'
        });
        const res = await fetch(
          `${API_BASE}/calculatedFields?${params.toString()}`,
          { headers: authHeaders(token) }
        );
        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Alarmregeln konnten nicht geladen werden (HTTP ${res.status})`);
        }
        const data = await res.json();
        const filtered = (data.data || []).filter(r => r.entityId?.id === deviceId);
        setAlarmRules(filtered);
      } catch (err) {
        setRulesError(err.message || "Alarmregeln konnten nicht geladen werden.");
      } finally {
        setRulesLoading(false);
      }
    }
    loadRules();
  }, [alarmTab, deviceId, token]);


  useEffect(() => {
    if (!loading && device && titleRef.current) {
      try {
        titleRef.current.focus();
      } catch {}
    }
  }, [loading, device]);

  function startEditing() {
    if (!device) return;
    setEditName(device.name || '');
    setEditVendorText(device.vendor || '');
    setEditSelectedVendor(device.vendor || '');
    setEditModelText(device.model || '');
    setEditSelectedModel(device.model || '');
    editOriginalRef.current = {
      name: device.name || '',
      vendor: device.vendor || '',
      model: device.model || '',
    };
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
  }

  async function saveEdit() {
    if (!editIsValid || !deviceId) return;
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE_IOTHUB}/api/v1/devices/${deviceId}`, {
        method: 'PATCH',
        headers: { ...authHeaders(iotHubToken), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editName.trim(),
          vendor: editSelectedVendor,
          model: editSelectedModel,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Gerät konnte nicht gespeichert werden (HTTP ${res.status})`);
      }
      setDevice(prev => ({ ...prev, name: editName.trim(), vendor: editSelectedVendor, model: editSelectedModel }));
      setEditing(false);
      showStatus('Gerätedetails wurden gespeichert.');
    } catch (err) {
      showStatus(`Gerätedetails konnten nicht gespeichert werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function ackAlarm(alarm) {
    await updateAlarmStatus(alarm, "ack", "ACTIVE_ACK");
  }

  async function clearAlarm(alarm) {
    await updateAlarmStatus(alarm, "clear", "CLEARED_ACK");
  }

  async function updateAlarmStatus(alarm, action, newStatus) {
    await updateAlarmStatusAPI(alarm.id.id, action, token);
    setAlarms(prev => prev.map(a => a.id.id === alarm.id.id ? { ...a, status: newStatus } : a));
    setOpenAlarmStatusMenu(null);
    const labels = { ACTIVE_ACK: 'Alarm bestätigt', CLEARED_ACK: 'Alarm behoben' };
    setStatusType('success');
    setStatusMessage(labels[newStatus] || 'Status geändert');
    setTimeout(() => setStatusMessage(''), 4000);
  }

  async function confirmDeleteAlarm() {
    if (!alarmToDelete) return;
    try {
      setDeletingAlarm(true);
      const res = await fetch(`${API_BASE}/alarm/${alarmToDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarm konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      setAlarms(prev => prev.filter(a => a.id.id !== alarmToDelete.id));
      showStatus(`Alarm „${alarmToDelete.name}" wurde gelöscht.`);
    } catch (err) {
      showStatus(`Alarm konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setDeletingAlarm(false);
      setShowAlarmDeleteConfirm(false);
      setAlarmToDelete(null);
    }
  }

  async function openAlarmRule(alarm) {
    try {
      const params = new URLSearchParams({ pageSize: '50', page: '0', types: 'ALARM', sortProperty: 'createdTime', sortOrder: 'DESC', textSearch: alarm.type || alarm.name || '' });
      const res = await fetch(`${API_BASE}/calculatedFields?${params.toString()}`, { headers: authHeaders(token) });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarmregel konnte nicht geladen werden (HTTP ${res.status})`);
      }
      const data = await res.json();
      const rules = data.data || [];
      const match = rules.find(r => r.name === (alarm.type || alarm.name) && r.entityId?.id === deviceId)
        || rules.find(r => r.name === (alarm.type || alarm.name));
      if (match) { navigate(`/alarm-rules/${match.id?.id}`); }
      else { showStatus('Zugehörige Alarmregel nicht gefunden.', 'error'); }
    } catch (err) { showStatus(`Alarmregel konnte nicht geladen werden: ${err.message || 'Netzwerkfehler'}.`, 'error'); }
  }

  async function confirmDeleteRule() {
    if (!ruleToDelete) return;
    try {
      setDeletingRule(true);
      const res = await fetch(`${API_BASE}/calculatedField/${ruleToDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarmregel konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      setAlarmRules(prev => prev.filter(r => r.id?.id !== ruleToDelete.id));
      showStatus(`Alarmregel „${ruleToDelete.name}" wurde gelöscht.`);
    } catch (err) {
      showStatus(`Alarmregel konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setDeletingRule(false);
      setShowRuleDeleteConfirm(false);
      setRuleToDelete(null);
    }
  }

  async function confirmDeleteDevice() {
    try {
      setDeletingDevice(true);
      const deleteRes = await fetch(
        `${API_BASE_IOTHUB}/api/v1/devices/${deviceId}`,
        { method: 'DELETE', headers: authHeaders(iotHubToken) }
      );
      if (!deleteRes.ok) throw new Error(`Löschen fehlgeschlagen (HTTP ${deleteRes.status})`);

      navigate('/devices', { state: { statusMessage: `Gerät „${device.name}“ wurde erfolgreich gelöscht.` } });
    } catch (e) {
      console.error(e);
      showStatus('Gerät konnte nicht gelöscht werden.', 'error');
    } finally {
      setDeletingDevice(false);
      setShowDeviceDeleteConfirm(false);
    }
  }

  const alarmsTotalPages = Math.max(1, Math.ceil(alarms.length / ALARM_PAGE_SIZE));
  const pagedAlarms = alarms.slice(alarmsPage * ALARM_PAGE_SIZE, (alarmsPage + 1) * ALARM_PAGE_SIZE);
  const rulesTotalPages = Math.max(1, Math.ceil(alarmRules.length / ALARM_PAGE_SIZE));
  const pagedRules = alarmRules.slice(rulesPage * ALARM_PAGE_SIZE, (rulesPage + 1) * ALARM_PAGE_SIZE);

  const editOrig = editOriginalRef.current;
  const editHasChanges = editing && editOrig != null && (
    editName !== editOrig.name ||
    editSelectedVendor !== editOrig.vendor ||
    editSelectedModel !== editOrig.model
  );
  const editIsValid = editName.trim() !== '' && !!editSelectedVendor && !!editSelectedModel;
  const editNameError = editing && !editName.trim();
  const editVendorError = editing && !editSelectedVendor;
  const editVendorErrorText = editVendorText.trim()
    ? 'Bitte einen Eintrag aus den Vorschlägen auswählen.'
    : 'Dieses Feld ist erforderlich.';
  const editModelError = editing && !editSelectedModel;
  const editModelErrorText = editModelText.trim()
    ? 'Bitte einen Eintrag aus den Vorschlägen auswählen.'
    : 'Dieses Feld ist erforderlich.';

  if (loading) return <p>Lade Details...</p>;
  if (error) return <div role="alert" className="error-message">{error}</div>;
  if (!device) return null;

  return (
    <div className="device-detail-page">

      {/* Header */}
      <div className="device-detail-header">
        <div className="header-left">
          <button
            className={"back-button"}
            onClick={()=> navigate(-1)}
            aria-label="Zurück zur vorherigen Seite"
            title="Zurück zur vorherigen Seite"
          >
            <span aria-hidden="true"><Zurück /></span>
          </button>
          <h1 id="device-title" tabIndex={-1} ref={titleRef} className="device-title">
            {device.name}
            <span className={`status-badge ${device.active ? "active" : "inactive"}`} aria-label={device.active ? "Aktiv" : "Inaktiv"}>
              {device.active ? "Aktiv" : "Inaktiv"}
            </span>
          </h1>
        </div>

        <div className="device-actions">
          
          <button
            type="button"
            className="action-buttons"
            aria-label="Daten exportieren"
            title="Daten exportieren"
            onClick={() => navigate('/export-data', { state: { prefillDeviceId: deviceId, prefillDeviceName: device.name } })}
          >
            <div className="action-button-icon"><Export /></div>
            <div>Daten exportieren</div>
          </button>

          <button
            className="action-buttons"
            aria-label="Daten importieren"
            title="Daten importieren"
            disabled
          >
            <div className="action-button-icon"><Import /></div>
            <div>Daten importieren</div>
          </button>

          <button
            className="action-buttons"
            aria-label="Daten analysieren"
            title="Daten analysieren"
            disabled
          >
            <div className="action-button-icon"><AiAnalyse2 /></div>
            <div>Daten analysieren</div>
          </button>

          <button
            type="button"
            className="action-buttons"
            aria-label="Downlink senden"
            title="Downlink senden"
            onClick={() => navigate(`/devices/${deviceId}/downlink`, { state: { name: device.name, fromDetails: true } })}
          >
            <div className="action-button-icon"><Downlink /></div>
            <div>Downlink senden</div>
          </button>

          <button
            className="action-buttons"
            aria-label="Endgerät löschen"
            title="Endgerät löschen"
            onClick={() => setShowDeviceDeleteConfirm(true)}
          >
            <div className="action-button-icon"><Löschen /></div>
            <div>Endgerät löschen</div>
          </button>

        </div>
      </div>

      {statusMessage && (
        <div className={`status-bar${statusType === 'error' ? ' status-bar--error' : ''}`} role="status" aria-live="polite">
          <p>{statusMessage}</p>
        </div>
      )}

      {/* Content Grid */}
      <main id="main-content" role="main" aria-labelledby="device-title" className="device-detail-grid">

        <div className="card-wrapper">
          <h2 className="card-title">Gerätedetails</h2>
          {/* Gerätedetails */}
          <section className={`card card-details${editing ? ' editing' : ' card-details-with-image'}`}>
            {editing ? (
              <div className="edit-form">
                {/* Name */}
                <div className={`edit-form-group${editName !== editOrig?.name ? ' changed' : ''}`}>
                  <label className="edit-form-label" htmlFor="edit-device-name">
                    Name<span className="required">*</span>
                    {editName !== editOrig?.name && <span className="changed-badge" aria-label="Geändert">• geändert</span>}
                  </label>
                  <input
                    type="text"
                    id="edit-device-name"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="Gerätename"
                    required
                  />
                  {editNameError && (
                    <p className="required-fields-error" role="alert">Dieses Feld ist erforderlich.</p>
                  )}
                </div>

                {/* Hersteller */}
                <div className="search-dropdown-group">
                  <SearchDropdown
                    id="vendor"
                    label="Hersteller"
                    required
                    inputValue={editVendorText}
                    onInputChange={text => { setEditVendorText(text); setEditSelectedVendor(''); setEditModelText(''); setEditSelectedModel(''); }}
                    options={vendorSuggestions}
                    onSelect={o => { setEditSelectedVendor(o.value); setEditVendorText(o.label); setEditModelText(''); setEditSelectedModel(''); }}
                    onClear={() => { setEditVendorText(''); setEditSelectedVendor(''); setEditModelText(''); setEditSelectedModel(''); }}
                    changed={editSelectedVendor !== editOrig?.vendor}
                  />
                  {editVendorError && (
                    <p className="required-fields-error" role="alert">{editVendorErrorText}</p>
                  )}
                </div>

                {/* Modell */}
                <div className="search-dropdown-group">
                  <SearchDropdown
                    id="model"
                    label="Modell"
                    required
                    inputValue={editModelText}
                    onInputChange={text => { setEditModelText(text); setEditSelectedModel(''); }}
                    options={modelSuggestions}
                    onSelect={o => { setEditSelectedModel(o.value); setEditModelText(o.label); }}
                    onClear={() => { setEditModelText(''); setEditSelectedModel(''); }}
                    disabled={!editSelectedVendor}
                    changed={editSelectedModel !== editOrig?.model}
                  />
                  {editModelError && (
                    <p className="required-fields-error" role="alert">{editModelErrorText}</p>
                  )}
                </div>

                <p className="required-hint">* Pflichtfeld</p>

                <div className="edit-form-actions">
                  <button className="btn-primary" onClick={saveEdit} disabled={saving || !editHasChanges || !editIsValid}>
                    <span className="icon" aria-hidden="true"><Speichern /></span>
                    {saving ? 'Speichern…' : 'Speichern'}
                  </button>
                  <button className="btn-secondary" onClick={cancelEditing} disabled={saving}>
                    <Abbrechen /> Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="card-details-content">
                <dl>
                  <dt>Name:</dt>
                  <dd>{device.name}</dd>

                  <dt>Gruppen:</dt>
                  <dd>{device.groups?.map(g => g.name).join(", ") || "–"}</dd>

                  <dt>Hersteller:</dt>
                  <dd>{device.vendor || "–"}</dd>

                  <dt>Modell:</dt>
                  <dd>{device.model || "–"}</dd>

                  <dt>Version:</dt>
                  <dd>{device.firmware || "–"}</dd>

                  <dt>EUI:</dt>
                  <dd>{device.cs_dev_eui || "–"}</dd>

                  <dt>Frequenz:</dt>
                  <dd>{device.region || "–"}</dd>

                  <dt>Erstellt am:</dt>
                  <dd>{formatDateDE(device.createdTime)}</dd>
                </dl>
                </div>

                <div className="card-details-image">
                  {deviceImageLoading ? (
                    <p>Lade Bild...</p>
                  ) : deviceImageUrl ? (
                    <img
                      src={deviceImageUrl}
                      alt={`Bild für ${device.model}`}
                      className="device-type-image"
                    />
                  ) : (
                    <p className="no-image-msg">Kein Bild für dieses Gerät verfügbar.</p>
                  )}
                </div>

                  <button
                    className="edit"
                    aria-label="Gerätedetails bearbeiten"
                    title="Gerätedetails bearbeiten"
                    onClick={startEditing}
                  >
                    <span className="action-button-icon"><Bearbeiten /></span>
                    <span>Bearbeiten</span>
                  </button>
              </>
            )}
          </section>
        </div>
        
        <div className="card-wrapper">
          <h2 className="card-title">Telemetriedaten</h2>
          {/* Telemetrie */}
          <section className="card telemetry-card" aria-live="polite" aria-busy={loading}>
            <div className="telemetry-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Gesendet</th>
                    <th scope="col">Schlüssel</th>
                    <th scope="col">Wert</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(telemetry).map(([key, values]) =>
                    values.map((v, idx) => (
                      <tr key={`${key}-${idx}`}>
                        <td>{formatTelemetryTimestamp(v.ts)}</td>
                        <td>{key}</td>
                        <td>{v.value}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="card-wrapper">
          <h2 className="card-title">Dashboards ({dashboards.length})</h2>
          {/* Dashboards */}
          <section className="card" aria-live="polite" aria-busy={loading}>
            {dashboards.map(d => (
              <div key={d.id.id} className="list-item">
                {d.title}
                <button aria-label={`Dashboard ${d.title} öffnen`}>
                  <span aria-hidden="true"><Öffnen /></span>
                </button>
              </div>
            ))}
            <button className="edit add-alarm"
              aria-label="Neues Dashboard erstellen"
              title="Neues Dashboard erstellen"
              disabled
            >
              ＋ Neues Dashboard erstellen
            </button>
          </section>
        </div>          
        
        <div className="card-wrapper">
          <div className="alarm-card-header">
            <h2 className="card-title">Alarme</h2>
            <div className="alarm-tabs" role="tablist" aria-label="Alarmansicht auswählen">
              <button
                role="tab"
                aria-selected={alarmTab === 'triggered'}
                className={`alarm-tab-btn${alarmTab === 'triggered' ? ' active' : ''}`}
                onClick={() => setAlarmTab('triggered')}
              >
                Ausgelöste Alarme ({alarms.length})
              </button>
              <button
                role="tab"
                aria-selected={alarmTab === 'rules'}
                className={`alarm-tab-btn${alarmTab === 'rules' ? ' active' : ''}`}
                onClick={() => setAlarmTab('rules')}
              >
                Alarmregeln
              </button>
            </div>
          </div>
          {/* Ausgelöste Alarme */}
          <section className="card" role="tabpanel" style={alarmTab !== 'triggered' ? {display:'none'} : undefined}>
            {pagedAlarms.map(a => (
              <div key={a.id.id} className="list-item alarm-item">

                <div className="alarm-main">
                  <span className="alarm-name">{a.name}</span>

                  <span className={`severity-badge ${a.severity.toLowerCase()}`}>
                    {severityLabels[a.severity]}
                  </span>
                </div>

                <div className="actions">

                  <AlarmStatusDropdown
                    alarmId={a.id.id}
                    status={a.status}
                    isOpen={openAlarmStatusMenu === a.id.id}
                    onToggle={() => setOpenAlarmStatusMenu(openAlarmStatusMenu === a.id.id ? null : a.id.id)}
                    onAck={() => ackAlarm(a)}
                    onClear={() => clearAlarm(a)}
                    onClose={() => setOpenAlarmStatusMenu(null)}
                  />

                  <button title="Details öffnen" aria-label="Details öffnen" onClick={() => openAlarmRule(a)}>
                    <span className="action-button-icon" aria-hidden="true"><Öffnen /></span>
                  </button>
                  <button title="Alarm löschen" aria-label="Alarm löschen" onClick={() => { setAlarmToDelete({ id: a.id.id, name: a.name }); setShowAlarmDeleteConfirm(true); }}>
                    <span className="action-button-icon" aria-hidden="true"><Löschen /></span>
                  </button>
                </div>

              </div>
            ))}

            {alarms.length > 0 && (
              <Pagination page={alarmsPage} totalPages={alarmsTotalPages} totalItems={alarms.length} pageSize={ALARM_PAGE_SIZE} onPageChange={setAlarmsPage} label="Alarme" />
            )}

          </section>

          {/* Alarmregeln */}
          <section className="card alarm-rules-panel" role="tabpanel" style={alarmTab !== 'rules' ? {display:'none'} : undefined}>
            {rulesLoading && <p>Lade Alarmregeln...</p>}
            {rulesError && <div role="alert">{rulesError}</div>}
            {!rulesLoading && !rulesError && alarmRules.length === 0 && (
              <p className="no-items-msg">Keine Alarmregeln für dieses Gerät gefunden.</p>
            )}
            {pagedRules.map(rule => {
              const severities = Object.keys(rule?.configuration?.createRules || {});
              return (
                <div key={rule.id?.id} className="list-item alarm-item">
                  <div className="alarm-main">
                    <span className="alarm-name">{rule.name}</span>
                    {severities.map(sev => (
                      <span key={sev} className={`severity-badge ${sev.toLowerCase()}`}>
                        {severityLabels[sev] || sev}
                      </span>
                    ))}
                  </div>
                  <div className="actions">
                    <button title="Alarmregel öffnen" aria-label={`Alarmregel ${rule.name} öffnen`} onClick={() => navigate(`/alarm-rules/${rule.id?.id}`)}>
                      <span className="action-button-icon" aria-hidden="true"><Öffnen /></span>
                    </button>
                    <button title="Alarmregel löschen" aria-label={`Alarmregel ${rule.name} löschen`} onClick={() => { setRuleToDelete({ id: rule.id?.id, name: rule.name }); setShowRuleDeleteConfirm(true); }}>
                      <span className="action-button-icon" aria-hidden="true"><Löschen /></span>
                    </button>
                  </div>
                </div>
              );
            })}

            <button className="edit add-alarm"
              aria-label="Neue Alarmregel erstellen"
              title="Neue Alarmregel erstellen"
              onClick={() => navigate('/new-alarm-rule', { state: { prefillDeviceId: deviceId, prefillDeviceName: device.name } })}
            >
              ＋ Neue Alarmregel erstellen
            </button>

            {alarmRules.length > 0 && (
              <Pagination page={rulesPage} totalPages={rulesTotalPages} totalItems={alarmRules.length} pageSize={ALARM_PAGE_SIZE} onPageChange={setRulesPage} label="Regeln" />
            )}
          </section>
        </div>

      </main>

      {/* Alarm Delete Confirmation Modal */}
      {showAlarmDeleteConfirm && alarmToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="del-alarm-title">
          <div className="modal" ref={alarmDeleteRef}>
            <h2 id="del-alarm-title">Alarm löschen?</h2>
            <p>Soll der Alarm <i>{alarmToDelete.name}</i> wirklich gelöscht werden? Diese Aktion ist <strong>unwiderruflich</strong>.</p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={confirmDeleteAlarm} disabled={deletingAlarm}>
                {deletingAlarm ? 'Löschen…' : '🗑 Löschen bestätigen'}
              </button>
              <button className="modal-secondary" onClick={() => { setShowAlarmDeleteConfirm(false); setAlarmToDelete(null); }} disabled={deletingAlarm}>
                ✕ Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alarm Rule Delete Confirmation Modal */}
      {showRuleDeleteConfirm && ruleToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="del-rule-title">
          <div className="modal" ref={ruleDeleteRef}>
            <h2 id="del-rule-title">Alarmregel löschen?</h2>
            <p>Soll die Alarmregel <i>{ruleToDelete.name}</i> wirklich gelöscht werden? Diese Aktion ist <strong>unwiderruflich</strong>.</p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={confirmDeleteRule} disabled={deletingRule}>
                {deletingRule ? 'Löschen…' : '🗑 Löschen bestätigen'}
              </button>
              <button className="modal-secondary" onClick={() => { setShowRuleDeleteConfirm(false); setRuleToDelete(null); }} disabled={deletingRule}>
                ✕ Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device Delete Confirmation Modal */}
      {showDeviceDeleteConfirm && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="del-device-title">
          <div className="modal" ref={deviceDeleteRef}>
            <h2 id="del-device-title">
              Sind Sie sicher, dass Sie das Gerät <br />
              <i>{device.name}</i> löschen möchten?
            </h2>
            <p>
              Nach der Bestätigung wird das Gerät und alle zugehörigen Daten <strong>unwiderruflich</strong> gelöscht.
            </p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={confirmDeleteDevice} disabled={deletingDevice}>
                {deletingDevice ? 'Löschen…' : <><Löschen /> Löschen bestätigen</>}
              </button>
              <button className="modal-secondary" onClick={() => setShowDeviceDeleteConfirm(false)} disabled={deletingDevice}>
                <Abbrechen /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceDetail;
